/**
 * @engine/input — Web Input Backend
 *
 * Browser-based input backend using:
 * - Keyboard: KeyboardEvent
 * - Mouse: MouseEvent, PointerEvent, WheelEvent
 * - Gamepad: Gamepad API (polling)
 * - Touch: TouchEvent
 * - Pen: PointerEvent (pointerType === 'pen')
 * - Sensors: DeviceMotionEvent, DeviceOrientationEvent
 */

import type { DeviceInfo, InputEvent, DeviceType, ControlValue } from '../types';
import { InputBackend } from './InputBackend';

// ============================================================================
// Known Gamepad Names
// ============================================================================

/** Map of vendor:product IDs to friendly names */
const KNOWN_GAMEPADS: Record<string, string> = {
  // Microsoft
  '045e:0b22': 'Xbox Elite 2',
  '045e:0b12': 'Xbox Series X',
  '045e:02fd': 'Xbox One S',
  '045e:02e0': 'Xbox One S (Bluetooth)',
  '045e:0b13': 'Xbox Series X (Bluetooth)',
  '045e:028e': 'Xbox 360',
  '045e:02d1': 'Xbox One',
  '045e:02ea': 'Xbox One S',
  // Sony
  '054c:0ce6': 'DualSense',
  '054c:0df2': 'DualSense Edge',
  '054c:09cc': 'DualShock 4 v2',
  '054c:05c4': 'DualShock 4 v1',
  '054c:0268': 'DualShock 3',
  // Nintendo
  '057e:2009': 'Switch Pro Controller',
  '057e:2006': 'Joy-Con (L)',
  '057e:2007': 'Joy-Con (R)',
  // 8BitDo
  '2dc8:2101': '8BitDo SN30 Pro+',
  '2dc8:6101': '8BitDo Ultimate',
  // Razer
  '1532:0a14': 'Razer Wolverine V2',
  '1532:0a03': 'Razer Wolverine Ultimate',
  // Logitech
  '046d:c21d': 'Logitech F310',
  '046d:c21e': 'Logitech F510',
  '046d:c21f': 'Logitech F710',
  // SteelSeries
  '1038:1412': 'SteelSeries Stratus Duo',
};

/**
 * Parsed gamepad info from raw ID string.
 */
interface ParsedGamepadInfo {
  name: string;
  vendorId?: number;
  productId?: number;
}

/**
 * Parse vendorId and productId from raw gamepad ID string.
 * Returns undefined if IDs cannot be parsed.
 */
function parseGamepadIds(rawId: string): { vendorId: number; productId: number } | undefined {
  const match = rawId.match(/Vendor:\s*([0-9a-fA-F]{4})\s*Product:\s*([0-9a-fA-F]{4})/i);
  if (match) {
    return {
      vendorId: parseInt(match[1], 16),
      productId: parseInt(match[2], 16),
    };
  }
  return undefined;
}

/**
 * Get a friendly name and parsed IDs for a gamepad from its raw ID string.
 * Falls back to extracting a reasonable name from the raw string.
 */
function parseGamepadInfo(rawId: string): ParsedGamepadInfo {
  const ids = parseGamepadIds(rawId);
  
  // Try to find a known name by vendor:product
  if (ids) {
    const key = `${ids.vendorId.toString(16).padStart(4, '0')}:${ids.productId.toString(16).padStart(4, '0')}`;
    const known = KNOWN_GAMEPADS[key];
    if (known) {
      return { name: known, ...ids };
    }
  }

  // Fallback: try to extract something meaningful from the raw string
  let name = 'Gamepad';
  if (rawId.includes('Xbox')) {
    if (rawId.includes('Elite')) name = 'Xbox Elite Controller';
    else if (rawId.includes('Series')) name = 'Xbox Series Controller';
    else name = 'Xbox Controller';
  } else if (rawId.includes('DualSense')) {
    name = 'DualSense';
  } else if (rawId.includes('DualShock') || rawId.includes('DUALSHOCK')) {
    name = 'DualShock';
  } else if (rawId.includes('Pro Controller')) {
    name = 'Switch Pro Controller';
  } else if (rawId.includes('Joy-Con')) {
    name = 'Joy-Con';
  } else if (rawId.includes('8BitDo')) {
    name = '8BitDo Controller';
  } else if (rawId.length <= 30) {
    // Short enough to use as-is
    const parenIdx = rawId.indexOf('(');
    if (parenIdx > 2) {
      name = rawId.substring(0, parenIdx).trim();
    } else if (rawId.trim()) {
      name = rawId;
    }
  }
  
  return { name, ...ids };
}

/**
 * Web browser input backend.
 * Attaches to DOM events and polls Gamepad API.
 */
export class WebInputBackend extends InputBackend {
  readonly name = 'WebInputBackend';

  /** Target element for input events (defaults to document) */
  private target: EventTarget;

  /** Bound event handlers (for cleanup) */
  private handlers: Array<[EventTarget, string, EventListener, boolean]> = [];

  /** Connected gamepads (tracked separately due to polling) */
  private gamepads = new Map<number, GamepadInfo>();

  /** Known devices */
  private devices = new Map<string, DeviceInfo>();

  constructor(target: EventTarget = document) {
    super();
    this.target = target;
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  async initialize(): Promise<void> {
    if (this._isActive) return;

    // Register keyboard (always present)
    this.registerDevice({
      id: 'keyboard/0',
      type: 'keyboard',
      name: 'Keyboard',
    });

    // Register mouse (always present)
    this.registerDevice({
      id: 'mouse/0',
      type: 'mouse',
      name: 'Mouse',
    });

    // Attach event listeners
    this.attachKeyboardEvents();
    this.attachMouseEvents();
    this.attachTouchEvents();
    this.attachGamepadEvents();
    this.attachSensorEvents();

    this._isActive = true;
  }

  shutdown(): void {
    if (!this._isActive) return;

    // Remove all event listeners
    for (const [target, event, handler, capture] of this.handlers) {
      target.removeEventListener(event, handler, capture);
    }
    this.handlers = [];

    // Clear devices
    this.devices.clear();
    this.gamepads.clear();
    this._heldKeys.clear();

    this._isActive = false;
  }

  poll(time: number): void {
    // Poll gamepad API (doesn't use events)
    this.pollGamepads(time);
  }

  getConnectedDevices(): DeviceInfo[] {
    return Array.from(this.devices.values());
  }

  // ============================================================================
  // Keyboard Events
  // ============================================================================

  /** Set of currently-held key codes (for blur reset) */
  private _heldKeys = new Set<string>();

  private attachKeyboardEvents(): void {
    // Use CAPTURE phase so stopPropagation() in editor components
    // cannot prevent keyboard events from reaching the input system.
    this.addListenerCapture(this.target, 'keydown', (e) => {
      const event = e as KeyboardEvent;
      if (event.repeat) return; // Ignore key repeat

      this._heldKeys.add(event.code);

      this.emitEvent({
        type: 'controlChanged',
        device: 'keyboard/0',
        control: event.code,
        value: true,
        time: event.timeStamp / 1000,
      });
    });

    this.addListenerCapture(this.target, 'keyup', (e) => {
      const event = e as KeyboardEvent;

      this._heldKeys.delete(event.code);

      this.emitEvent({
        type: 'controlChanged',
        device: 'keyboard/0',
        control: event.code,
        value: false,
        time: event.timeStamp / 1000,
      });
    });

    // When the window loses focus (e.g., Alt-Tab, click outside Electron),
    // release all held keys so they don't get stuck.
    this.addListener(window, 'blur', () => {
      const time = performance.now() / 1000;
      for (const code of this._heldKeys) {
        this.emitEvent({
          type: 'controlChanged',
          device: 'keyboard/0',
          control: code,
          value: false,
          time,
        });
      }
      this._heldKeys.clear();
    });
  }

  // ============================================================================
  // Mouse Events
  // ============================================================================

  private attachMouseEvents(): void {
    // Position & delta via pointer events (better than mouse events)
    this.addListener(this.target, 'pointermove', (e) => {
      const event = e as PointerEvent;
      if (event.pointerType === 'pen') {
        this.handlePenEvent(event);
        return;
      }
      if (event.pointerType !== 'mouse') return;

      const time = event.timeStamp / 1000;

      this.emitEvent({
        type: 'controlChanged',
        device: 'mouse/0',
        control: 'position',
        value: { x: event.clientX, y: event.clientY },
        time,
      });

      this.emitEvent({
        type: 'controlChanged',
        device: 'mouse/0',
        control: 'delta',
        value: { x: event.movementX, y: event.movementY },
        time,
      });
    });

    // Buttons
    this.addListener(this.target, 'pointerdown', (e) => {
      const event = e as PointerEvent;
      if (event.pointerType === 'pen') {
        this.handlePenEvent(event);
        return;
      }
      if (event.pointerType !== 'mouse') return;

      this.emitEvent({
        type: 'controlChanged',
        device: 'mouse/0',
        control: this.mouseButtonName(event.button),
        value: true,
        time: event.timeStamp / 1000,
      });
    });

    this.addListener(this.target, 'pointerup', (e) => {
      const event = e as PointerEvent;
      if (event.pointerType === 'pen') {
        this.handlePenEvent(event);
        return;
      }
      if (event.pointerType !== 'mouse') return;

      this.emitEvent({
        type: 'controlChanged',
        device: 'mouse/0',
        control: this.mouseButtonName(event.button),
        value: false,
        time: event.timeStamp / 1000,
      });
    });

    // Scroll
    this.addListener(this.target, 'wheel', (e) => {
      const event = e as WheelEvent;
      const time = event.timeStamp / 1000;
      
      // Emit as Vec2 for axis2d bindings
      this.emitEvent({
        type: 'controlChanged',
        device: 'mouse/0',
        control: 'scroll',
        value: { x: event.deltaX, y: event.deltaY },
        time,
      });
      
      // Also emit individual axes for value bindings
      if (event.deltaX !== 0) {
        this.emitEvent({
          type: 'controlChanged',
          device: 'mouse/0',
          control: 'scroll/x',
          value: Math.sign(event.deltaX), // Normalize to -1/0/1
          time,
        });
      }
      if (event.deltaY !== 0) {
        this.emitEvent({
          type: 'controlChanged',
          device: 'mouse/0',
          control: 'scroll/y',
          value: Math.sign(event.deltaY), // Normalize to -1/0/1
          time,
        });
      }
    });
  }

  private mouseButtonName(button: number): string {
    switch (button) {
      case 0:
        return 'leftButton';
      case 1:
        return 'middleButton';
      case 2:
        return 'rightButton';
      case 3:
        return 'backButton';
      case 4:
        return 'forwardButton';
      default:
        return `button${button}`;
    }
  }

  // ============================================================================
  // Touch Events
  // ============================================================================

  private attachTouchEvents(): void {
    const handleTouches = (e: Event, phase: 'began' | 'moved' | 'ended' | 'canceled') => {
      const event = e as TouchEvent;
      // Register touchscreen device on first touch
      if (!this.devices.has('touchscreen/0')) {
        this.registerDevice({
          id: 'touchscreen/0',
          type: 'touchscreen',
          name: 'Touchscreen',
        });
      }

      const time = event.timeStamp / 1000;

      // Emit touch count
      this.emitEvent({
        type: 'controlChanged',
        device: 'touchscreen/0',
        control: 'touchCount',
        value: event.touches.length,
        time,
      });

      // Emit each changed touch
      const changedTouches = phase === 'ended' || phase === 'canceled'
        ? event.changedTouches
        : event.touches;

      for (let i = 0; i < changedTouches.length; i++) {
        const touch = changedTouches[i];
        const touchId = touch.identifier % 10; // Clamp to 0-9

        this.emitEvent({
          type: 'controlChanged',
          device: 'touchscreen/0',
          control: `touch${touchId}`,
          value: {
            id: touchId,
            phase,
            position: { x: touch.clientX, y: touch.clientY },
            delta: { x: 0, y: 0 }, // Computed by device
            pressure: (touch as unknown as { force?: number }).force ?? 1,
            radius: {
              x: touch.radiusX ?? 0,
              y: touch.radiusY ?? 0,
            },
          },
          time,
        });
      }
    };

    this.addListener(this.target, 'touchstart', (e) => handleTouches(e, 'began'));
    this.addListener(this.target, 'touchmove', (e) => handleTouches(e, 'moved'));
    this.addListener(this.target, 'touchend', (e) => handleTouches(e, 'ended'));
    this.addListener(this.target, 'touchcancel', (e) => handleTouches(e, 'canceled'));
  }

  // ============================================================================
  // Pen Events
  // ============================================================================

  private handlePenEvent(event: PointerEvent): void {
    // Register pen device on first use
    if (!this.devices.has('pen/0')) {
      this.registerDevice({
        id: 'pen/0',
        type: 'pen',
        name: 'Pen',
      });
    }

    const time = event.timeStamp / 1000;

    // Emit pen state as PenEventData
    this.emitEvent({
      type: 'controlChanged',
      device: 'pen/0',
      control: 'state',
      value: {
        position: { x: event.clientX, y: event.clientY },
        pressure: event.pressure,
        tiltX: event.tiltX,
        tiltY: event.tiltY,
        twist: event.twist,
      },
      time,
    });

    // Also emit individual controls
    this.emitEvent({
      type: 'controlChanged',
      device: 'pen/0',
      control: 'position',
      value: { x: event.clientX, y: event.clientY },
      time,
    });

    this.emitEvent({
      type: 'controlChanged',
      device: 'pen/0',
      control: 'pressure',
      value: event.pressure,
      time,
    });

    this.emitEvent({
      type: 'controlChanged',
      device: 'pen/0',
      control: 'tip',
      value: event.pressure > 0 && (event.buttons & 1) !== 0,
      time,
    });
  }

  // ============================================================================
  // Gamepad Events (Polling-based)
  // ============================================================================

  private attachGamepadEvents(): void {
    // Gamepad connect/disconnect
    this.addListener(window, 'gamepadconnected', (e) => {
      const event = e as GamepadEvent;
      this.onGamepadConnected(event.gamepad);
    });

    this.addListener(window, 'gamepaddisconnected', (e) => {
      const event = e as GamepadEvent;
      this.onGamepadDisconnected(event.gamepad);
    });
  }

  private onGamepadConnected(gamepad: Gamepad): void {
    const info: GamepadInfo = {
      index: gamepad.index,
      id: gamepad.id,
      mapping: gamepad.mapping,
    };
    this.gamepads.set(gamepad.index, info);

    const parsed = parseGamepadInfo(gamepad.id);
    const deviceInfo: DeviceInfo = {
      id: `gamepad/${gamepad.index}`,
      type: 'gamepad',
      name: parsed.name,
      vendorId: parsed.vendorId,
      productId: parsed.productId,
      capabilities: {
        rumble: 'vibrationActuator' in gamepad,
      },
    };
    this.registerDevice(deviceInfo);
  }

  private onGamepadDisconnected(gamepad: Gamepad): void {
    this.gamepads.delete(gamepad.index);

    const parsed = parseGamepadInfo(gamepad.id);
    const deviceInfo: DeviceInfo = {
      id: `gamepad/${gamepad.index}`,
      type: 'gamepad',
      name: parsed.name,
      vendorId: parsed.vendorId,
      productId: parsed.productId,
    };
    this.unregisterDevice(deviceInfo);
  }

  private pollGamepads(time: number): void {
    const gamepads = navigator.getGamepads();

    for (const gamepad of gamepads) {
      if (!gamepad) continue;

      const deviceId = `gamepad/${gamepad.index}`;

      // If not tracked yet, register it
      if (!this.devices.has(deviceId)) {
        this.onGamepadConnected(gamepad);
      }

      // Emit control updates
      this.emitEvent({
        type: 'controlChanged',
        device: deviceId,
        control: '__raw__',
        value: {
          buttons: gamepad.buttons.map((b) => ({ pressed: b.pressed, value: b.value })),
          axes: Array.from(gamepad.axes),
        },
        time,
      });
    }
  }

  // ============================================================================
  // Sensor Events
  // ============================================================================

  private attachSensorEvents(): void {
    // DeviceMotion for accelerometer and gyroscope
    this.addListener(window, 'devicemotion', (e) => {
      const event = e as DeviceMotionEvent;
      const time = event.timeStamp / 1000;

      // Accelerometer
      const accel = event.accelerationIncludingGravity;
      if (accel) {
        if (!this.devices.has('accelerometer/0')) {
          this.registerDevice({
            id: 'accelerometer/0',
            type: 'accelerometer',
            name: 'Accelerometer',
          });
        }

        this.emitEvent({
          type: 'controlChanged',
          device: 'accelerometer/0',
          control: '__raw__',
          value: {
            accelerationIncludingGravity: accel,
            acceleration: event.acceleration ?? undefined,
          },
          time,
        });
      }

      // Gyroscope
      const rotation = event.rotationRate;
      if (rotation) {
        if (!this.devices.has('gyroscope/0')) {
          this.registerDevice({
            id: 'gyroscope/0',
            type: 'gyroscope',
            name: 'Gyroscope',
          });
        }

        this.emitEvent({
          type: 'controlChanged',
          device: 'gyroscope/0',
          control: '__raw__',
          value: { rotationRate: rotation },
          time,
        });
      }
    });
  }

  // ============================================================================
  // Pointer Lock
  // ============================================================================

  override async requestPointerLock(): Promise<boolean> {
    try {
      const element = this.target as Element;
      if ('requestPointerLock' in element) {
        await element.requestPointerLock();
        return true;
      }
    } catch {
      // Ignore errors
    }
    return false;
  }

  override exitPointerLock(): void {
    document.exitPointerLock();
  }

  override isPointerLocked(): boolean {
    return document.pointerLockElement != null;
  }

  // ============================================================================
  // Haptics
  // ============================================================================

  override triggerHaptic(deviceId: string, intensity: number, duration: number): void {
    const match = deviceId.match(/^gamepad\/(\d+)$/);
    if (!match) return;

    const index = parseInt(match[1], 10);
    const gamepad = navigator.getGamepads()[index];
    if (!gamepad) return;

    // Try W3C Gamepad Extensions API
    const actuator = (gamepad as unknown as { vibrationActuator?: VibrationActuator }).vibrationActuator;
    if (actuator) {
      actuator.playEffect('dual-rumble', {
        duration,
        strongMagnitude: intensity,
        weakMagnitude: intensity * 0.5,
      }).catch(() => {/* Ignore */});
    }
  }

  // ============================================================================
  // Helpers
  // ============================================================================

  private addListener(target: EventTarget, event: string, handler: EventListener): void {
    target.addEventListener(event, handler);
    this.handlers.push([target, event, handler, false]);
  }

  /** Like addListener but uses capture phase (fires before bubbling) */
  private addListenerCapture(target: EventTarget, event: string, handler: EventListener): void {
    target.addEventListener(event, handler, true);
    this.handlers.push([target, event, handler, true]);
  }

  private registerDevice(info: DeviceInfo): void {
    if (this.devices.has(info.id)) return;
    this.devices.set(info.id, info);
    this.emitDeviceConnected(info);
  }

  private unregisterDevice(info: DeviceInfo): void {
    if (!this.devices.has(info.id)) return;
    this.devices.delete(info.id);
    this.emitDeviceDisconnected(info);
  }
}

// ============================================================================
// Internal Types
// ============================================================================

interface GamepadInfo {
  index: number;
  id: string;
  mapping: GamepadMappingType;
}

interface VibrationActuator {
  playEffect(
    type: 'dual-rumble',
    options: {
      duration: number;
      strongMagnitude: number;
      weakMagnitude: number;
    }
  ): Promise<void>;
}
